import React, { Fragment, useRef, useState } from "react"

import { usePopper } from "react-popper"
import { Portal } from "react-portal"

import { Listbox, Transition } from "@headlessui/react"
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid"

import { SelectOption } from "~tailwindui/types/SelectOption"

import ErrorIcon from "~tailwindui/Basics/ErrorIcon"

import classNames from "src/classNames"

export type MultiSelectProps = {
  label: string
  name: string
  options: SelectOption[]
  onChange: (value: string[]) => void
  error?: string
  value?: string[]
  description?: string
  valueAttribute?: string
  subfield?: boolean
  portal?: boolean
  inputWrapperClassNames?: string
  horizontal?: boolean
}
const MultiSelect: React.FC<
  MultiSelectProps & React.SelectHTMLAttributes<HTMLSelectElement>
> = ({
  label,
  name,
  value,
  options,
  onChange,
  error = "",
  description = "",
  valueAttribute = "value",
  subfield = false,
  portal = false,
  inputWrapperClassNames = "",
  horizontal = false,
  ...props
}) => {
  const selectedOptions = options.filter(opt =>
    value.includes(opt[valueAttribute])
  )
  const removeItem = (optionToRemove: SelectOption) => {
    const newOpts = selectedOptions.filter(
      opt => opt[valueAttribute] !== optionToRemove[valueAttribute]
    )
    onChange(newOpts as any[])
  }
  const popperElRef = useRef(null)
  const [targetElement, setTargetElement] = useState(null)
  const [popperElement, setPopperElement] = useState(null)
  const { styles, attributes } = usePopper(targetElement, popperElement, {
    placement: "bottom-start",
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [0, 8],
        },
      },
    ],
  })

  const items = open => {
    let divAttributes = {}
    if (portal) {
      divAttributes = {
        ref: popperElRef,
        style: { zIndex: 100, ...styles.popper },
        ...attributes.popper,
      }
    }

    const content = (
      <div {...divAttributes}>
        <Transition
          show={open}
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
          beforeEnter={() => setPopperElement(popperElRef.current)}
          afterLeave={() => setPopperElement(null)}
        >
          <Listbox.Options
            className={classNames(
              portal ? "-top-2.5 w-auto" : "w-full",
              "absolute z-10 mt-1 max-h-60 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
            )}
          >
            {options.map(option => (
              <Listbox.Option
                key={`${option.label}-${option.sublabel}`}
                className={({ active }) =>
                  classNames(
                    active ? "bg-sky-600 text-white" : "text-gray-900",
                    "relative cursor-default select-none py-2 pl-3 pr-9"
                  )
                }
                value={option}
              >
                {({ selected, active }) => (
                  <>
                    <div className="flex">
                      <span
                        className={classNames(
                          selected ? "font-semibold" : "font-normal",
                          "truncate"
                        )}
                      >
                        {option.label}
                      </span>

                      <span
                        className={classNames(
                          active ? "text-sky-200" : "text-gray-500",
                          "ml-2 truncate"
                        )}
                      >
                        {option.sublabel}
                      </span>
                    </div>

                    {selected ? (
                      <span
                        className={classNames(
                          active ? "text-white" : "text-sky-600",
                          "absolute inset-y-0 right-0 flex items-center pr-4"
                        )}
                      >
                        <CheckIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    )

    if (portal) {
      return <Portal>{content}</Portal>
    } else {
      return content
    }
  }

  return (
    <div
      className={
        horizontal
          ? "sm:grid sm:grid-cols-3 sm:items-start sm:gap-4 sm:py-6"
          : "mb-3"
      }
    >
      <Listbox
        multiple
        value={selectedOptions as any}
        onChange={onChange}
        name={name}
        {...props}
      >
        {({ open }) => (
          <>
            <label
              htmlFor={name}
              className={classNames(
                subfield && "ml-8",
                "block text-sm font-medium leading-6 text-gray-900 sm:pt-1.5"
              )}
            >
              {label}
            </label>

            <div
              className={classNames(
                subfield && "ml-8",
                !horizontal && "mt-1",
                "relative",
                inputWrapperClassNames
              )}
            >
              <div ref={setTargetElement}>
                <Listbox.Button
                  className={classNames(
                    "relative min-h-[2.25rem] w-full cursor-default rounded-md bg-white py-1.5 pl-3 pr-10 text-left text-gray-900 shadow-sm ring-1 ring-inset focus:outline-none focus:ring-2 focus:ring-sky-600 sm:text-sm sm:leading-6",
                    error ? "ring-red-300" : "ring-gray-300"
                  )}
                >
                  <div className="flex flex-auto flex-wrap">
                    {selectedOptions.map(option => (
                      <div
                        key={option[valueAttribute]}
                        className="flex items-center justify-center rounded-full border border-blue-300 bg-white bg-blue-100 py-[.2rem] px-2 font-medium text-blue-700 "
                      >
                        <div className="max-w-full flex-initial text-xs font-normal leading-none">
                          {option.label}
                        </div>
                        <div
                          className="flex flex-auto flex-row-reverse"
                          onClick={() => removeItem(option)}
                        >
                          <div>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="100%"
                              height="100%"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="feather feather-x ml-2 h-4 w-4 cursor-pointer rounded-full hover:text-blue-400"
                            >
                              <line x1="18" y1="6" x2="6" y2="18"></line>
                              <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                    {error ? (
                      <ErrorIcon />
                    ) : (
                      <ChevronUpDownIcon
                        className="h-5 w-5 text-gray-400"
                        aria-hidden="true"
                      />
                    )}
                  </span>
                </Listbox.Button>
              </div>

              {items(open)}
            </div>
          </>
        )}
      </Listbox>

      <div
        className={classNames(
          "text-sm",
          horizontal ? "flex h-full items-center" : "mt-1"
        )}
      >
        {error ? (
          <p className="text-red-600">{error}</p>
        ) : (
          <p className="text-gray-500">{description}</p>
        )}
      </div>
    </div>
  )
}

export default MultiSelect
